import { useVirtualizer } from '@tanstack/react-virtual';
import { Option } from '@ui/types/common.type';
import useBoolean from '@util/hooks/use-boolean/use-boolean.hook';
import useScreenResizeListener, {
  ScreenSizeOption,
} from '@util/hooks/use-screen-resize-listener/use-screen-resize-listener.hook';
import { ComponentPropsWithoutRef, useCallback, useEffect, useMemo, useRef } from 'react';

// #region Props
export type SidebarMenu = Option & ComponentPropsWithoutRef<'button'>;

export type UseSidebarHandlerProps = {
  setCloseSidebarHandler?: (callback: VoidFunction) => void;
  sidebarMenus?: SidebarMenu[];
};
// #endregion

/**
 * Hook for handling sidebar interactions and state in the dashboard layout
 * Manages sidebar open/close state and responsive behavior
 */
export default function useSidebarHandler({
  setCloseSidebarHandler,
  sidebarMenus,
}: UseSidebarHandlerProps = {}) {
  // #region Ref and State
  const [open, { setIsOn, toggle, turnOff }] = useBoolean(window.innerWidth > ScreenSizeOption.MD);

  const menuContainerRef = useRef<HTMLElement>(null);
  // #endregion

  // #region Computed Values
  const style = useMemo(() => ({ width: `${open ? 220 : 0}px` }), [open]);
  // #endregion

  // #region Handlers
  const handleScreenResize = useCallback(
    (width: number) => setIsOn(width >= ScreenSizeOption.MD),
    [setIsOn],
  );

  const handleSidebarMenuClick = useCallback(() => {
    if (window.innerWidth < ScreenSizeOption.MD) turnOff();
  }, [turnOff]);
  // #endregion

  // #region Effects
  useEffect(() => {
    setCloseSidebarHandler?.(turnOff);
  }, [setCloseSidebarHandler, turnOff]);

  useScreenResizeListener({ listener: handleScreenResize });
  // #endregion

  // #region Menu Virtualization
  const menuVirtualizer = useVirtualizer({
    count: sidebarMenus?.length ?? 0,
    // Item height is 40px
    estimateSize: () => 40,
    // Item gap is 6px
    gap: 6,
    getScrollElement: () => menuContainerRef.current,
  });
  // #endregion

  return {
    handleSidebarMenuClick,
    menuContainerRef,
    menuHeight: menuVirtualizer.getTotalSize(),
    menuItemRef: menuVirtualizer.measureElement,
    menuVirtualItems: menuVirtualizer.getVirtualItems(),
    open,
    style,
    toggle,
  };
}
